import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { Dispatch } from 'redux'
import { History } from 'history'
import {
  getLocalStorageRefreshToken,
  getLocalStorageUserToken,
  setLocalStorageRefreshToken,
  setLocalStorageUserToken
} from '../utils/localStorage'
import { ApplicationState } from '../store'
import { setUserLoading, userLogin, userLogout } from '../store/user/actions'
import { fetchUserData } from '../api/auth'
import { getUrlWithSalonGuid } from '../utils/urlParams'
import { getUserDataWithRefreshToken } from '../api/auth/refreshToken'
import { RouteType } from '../types/routes'

type CheckUserStatusProps = {
  dispatch: Dispatch
  history?: History
  redirectTo?: RouteType
}

export const checkUserStatus = async ({ history, dispatch, redirectTo }: CheckUserStatusProps) => {
  const handleRejection = () => {
    dispatch(userLogout())
    if (history && redirectTo) {
      history.push(getUrlWithSalonGuid(redirectTo))
    }
  }

  const checkRefreshToken = async () => {
    const refreshToken = getLocalStorageRefreshToken()

    // refresh token not found
    if (!refreshToken) {
      handleRejection()
      return
    }

    const refreshTokenResponse = await getUserDataWithRefreshToken(refreshToken)

    // server error
    if (!refreshTokenResponse) {
      handleRejection()
      return
    }

    // refresh token is invalid
    if (('status' in refreshTokenResponse && refreshTokenResponse.status === 'Unauthorized') || !('token' in refreshTokenResponse)) {
      handleRejection()
      return
    }

    // refresh token is valid
    if ('token' in refreshTokenResponse && 'benutzer' in refreshTokenResponse) {
      dispatch(userLogin(refreshTokenResponse.benutzer))
      setLocalStorageUserToken(refreshTokenResponse.token)
      setLocalStorageRefreshToken(refreshTokenResponse.refreshToken)
      return
    }

    // error in server response
    handleRejection()
  }

  const userToken = getLocalStorageUserToken()

  dispatch(setUserLoading())
  // user token not found
  if (!userToken) {
    await checkRefreshToken()
    return
  }

  // user token found
  const fetchedUserData = await fetchUserData(userToken)

  if (fetchedUserData !== null) {
    // token in localStorage is valid
    dispatch(userLogin(fetchedUserData))
  } else {
    // token in localStorage is invalid, check refresh token
    await checkRefreshToken()
  }
}

/**
 * Custom hook that checks if the user is logged in.
 *
 * If the user is logged in, it does nothing.
 *
 * If the user is not logged in, it checks for the token in localStorage.
 *
 * If the token is not found, it performs a logout and redirects to the specified URL if given.
 *
 * If the token is found, it performs a request to the backend to check if the token is valid.
 *
 * If the token is valid, it performs a login.
 *
 * If the token is invalid, it performs a logout and redirects to the specified URL if given.
 *
 * @param redirectTo - The URL to redirect to if the user is not logged in.
 * @returns null
 */
export const useUserLoggedInCheck = (redirectTo?: RouteType) => {
  const isUserLoggedIn = useSelector((state: ApplicationState) => state.user.isLoggedIn)
  const history = useHistory()
  const dispatch = useDispatch()

  useEffect(() => {
    if (isUserLoggedIn) {
      return
    }
    checkUserStatus({ history, dispatch, redirectTo })
  }, [])
}
